import React, { FC } from 'react';
import Typography from '@components/common/typography';
import Input from '@components/common/input';
import Button from '@components/common/button';
import styles from './styles.module.scss';
import { routes } from '@const/routes';
import { useForm } from '@hooks/helpers/useForm';
import FormField from '@components/form_field';
import { zodResolver } from '@hookform/resolvers/zod';
import { signInValidation } from '@helpers/validation_objects/signin';
import { defaultSignInData } from '@const/default_form_data';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiAuthService, apiUserService } from '@api';
import { useLocales } from '@hooks/helpers/useLocales';
import { useTranslation } from 'react-i18next';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { EUserRole } from '@xeppt/xeppt-sdk/types';
import { useUserContext } from '@hooks/context/useUserContext';
import { useQueryParams } from '@hooks/helpers/useQueryParams';

interface IProps {
    isForgot?: boolean;
}

const SignInLayout: FC<IProps> = ({ isForgot }) => {
    const { t } = useTranslation('sign_in');
    const [token] = useQueryParams(['token']);
    const { validationLocale, requestSuccessLocale, labelLocale, requestErrorLocale } =
        useLocales();
    const { handleLogout } = useUserContext();
    const navigate = useNavigate();
    const {
        control,
        formState: { isValid },
        getValues
    } = useForm({
        defaultValues: defaultSignInData,
        resolver: !token
            ? zodResolver(
                  signInValidation({
                      messages: {
                          email: validationLocale('email')
                      }
                  })
              )
            : undefined
    });

    const { handleRequest: handleSubmitForm, isLoading } = useApiMutation({
        method: () => {
            const { email, password } = getValues();
            return apiAuthService.login({
                email_or_phone: email,
                password
            });
        },
        onSuccess: () => {
            apiUserService.profile().then(({ role }) => {
                if (role === EUserRole.REGULAR_USER) {
                    handleLogout();
                    toast.error(t('requests.error.role'));
                } else {
                    toast.success(requestSuccessLocale('sign_in'));
                    navigate(routes.transactions);
                }
            });
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleForgotPassword, isLoading: isForgotPasswordLoading } =
        useApiMutation({
            method: () => {
                const { email } = getValues();
                return apiAuthService.forgotPassword(email);
            },
            onSuccess: () => {
                toast.success('Link was successfully sent to your email');
            },
            onError: requestErrorLocale
        });

    const { handleRequest: handleResetPassword, isLoading: isResetPasswordLoading } =
        useApiMutation({
            method: () => {
                const value = getValues();
                return apiAuthService.resetPassword(token || '', value.password, value.password);
            },
            onSuccess: () => {
                toast.success('Password was successfully reset');
            },
            onError: requestErrorLocale
        });

    return (
        <div className={styles.wrapper}>
            <Typography className={styles.title} variant="h3">
                {isForgot ? 'Forgot password' : t('title')}
            </Typography>
            <div className={styles.form}>
                {!token && (
                    <FormField
                        // react-hook-form >=7.6x makes Control<T> invariant on its validate
                        // member, so a typed control no longer assigns to Control<any>.
                        control={control as never}
                        name="email"
                        renderComponent={(props) => (
                            <Input {...props} full label={labelLocale('email')} name="email" />
                        )}
                    />
                )}
                {(!isForgot || token) && (
                    <FormField
                        control={control as never}
                        name="password"
                        renderComponent={(props) => (
                            <Input
                                {...props}
                                full
                                type="password"
                                name="password"
                                label={labelLocale('password')}
                            />
                        )}
                    />
                )}
                <Button
                    onClick={() => navigate(isForgot ? routes.signin : routes.forgot)}
                    size="small">
                    {isForgot ? 'Sign in' : 'Reset password'}
                </Button>
            </div>
            <Button
                onClick={() =>
                    isForgot
                        ? token
                            ? handleResetPassword(undefined)
                            : handleForgotPassword(undefined)
                        : handleSubmitForm(undefined)
                }
                isLoading={isLoading || isForgotPasswordLoading || isResetPasswordLoading}
                disabled={!isValid}
                variant="primary"
                size="normal">
                {isForgot ? 'Reset' : t('submit')}
            </Button>
        </div>
    );
};
export default SignInLayout;
